import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties
} from "react";
import { Pause, Play, RotateCcw, X } from "lucide-react";
import { getAsset } from "../../config/assets";
import { assetUrl } from "../../utils/assetUrl";
import "./NightTomatoReactionDemo.css";

const TRIGGER_TIME_SECONDS = 6.75;
const PROMPT_TIMEOUT_MS = 15_000;
const IMPACT_DELAY_MS = 620;
const RESUME_AFTER_THROW_MS = 2_220;
const CARD_EXIT_DURATION_MS = 800;
const EFFECT_LIFETIME_MS = 2_250;
const MAX_THROWS = 3;

type TomatoVariant = "red" | "gold";

interface TomatoThrowEffect {
  id: number;
  variant: TomatoVariant;
  startX: number;
  startY: number;
  midX: number;
  midY: number;
  targetX: number;
  targetY: number;
  startRotation: number;
  endRotation: number;
}

type MotionStyle = CSSProperties & Record<`--tomato-${string}`, string>;

const THROW_ORIGINS = [
  { x: 2680, y: 1240, curveX: -120, rotation: -300 },
  { x: -140, y: 1180, curveX: 150, rotation: 280 },
  { x: 2240, y: 1720, curveX: -230, rotation: -360 }
] as const;

function requireInteractionAsset(id: string) {
  const path = getAsset(id, "video-interaction-v1")?.path;

  if (!path) {
    throw new Error(`Missing video interaction asset: ${id}`);
  }

  return path;
}

const TOMATO_ASSETS = {
  clip: requireInteractionAsset("tomato-trigger-video"),
  projectileRed: requireInteractionAsset("tomato-projectile-red"),
  projectileGold: requireInteractionAsset("tomato-projectile-gold"),
  splashRed: requireInteractionAsset("tomato-impact-splash"),
  splashGold: requireInteractionAsset("tomato-impact-splash-gold"),
  whoosh: requireInteractionAsset("tomato-throw-whoosh"),
  impactRed: requireInteractionAsset("tomato-impact"),
  impactGold: requireInteractionAsset("tomato-impact-gold")
} as const;

function getFaceTarget(currentTime: number) {
  if (currentTime < 5.5) {
    return { x: 980, y: 500 };
  }

  if (currentTime < 11) {
    return { x: 1940, y: 500 };
  }

  return { x: 1920, y: 500 };
}

function TomatoActionCard({
  evaSrc,
  message,
  actionLabel,
  actionAriaLabel,
  onAction,
  comboCount,
  disabled = false,
  isExiting = false
}: {
  evaSrc: string;
  message: string;
  actionLabel: string;
  actionAriaLabel: string;
  onAction: () => void;
  comboCount?: number;
  disabled?: boolean;
  isExiting?: boolean;
}) {
  const copy = (
    <span className="night-tomato-prompt__copy">
      <span className="night-tomato-prompt__eyebrow">
        <small>EVA</small>
        {comboCount ? (
          <span
            className={`night-tomato-prompt__combo${comboCount === MAX_THROWS ? " night-tomato-prompt__combo--gold" : ""}`}
          >
            x{comboCount}
          </span>
        ) : null}
      </span>
      <strong>{message}</strong>
    </span>
  );
  const actionContent = actionLabel;

  if (!comboCount) {
    return (
      <button className="night-tomato-prompt" type="button" onClick={onAction}>
        <img className="night-tomato-prompt__eva" src={evaSrc} alt="" draggable={false} />
        {copy}
        <span className="night-tomato-prompt__action">{actionContent}</span>
      </button>
    );
  }

  return (
    <div
      className={`night-tomato-prompt night-tomato-prompt--combo${isExiting ? " night-tomato-prompt--exiting" : ""}`}
      aria-live="polite"
    >
      <img className="night-tomato-prompt__eva" src={evaSrc} alt="" draggable={false} />
      {copy}
      <button
        className="night-tomato-prompt__action night-tomato-prompt__action--button"
        type="button"
        onClick={onAction}
        disabled={disabled}
        aria-label={actionAriaLabel}
      >
        {actionContent}
      </button>
    </div>
  );
}

export function NightTomatoReactionDemo({
  evaSrc,
  onClose
}: {
  evaSrc: string;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const promptTimeoutRef = useRef<number | null>(null);
  const resumeVideoTimerRef = useRef<number | null>(null);
  const resumePlaybackRef = useRef(false);
  const scheduledTimersRef = useRef<Set<number>>(new Set());
  const activeAudioRef = useRef<Set<HTMLAudioElement>>(new Set());
  const promptShownRef = useRef(false);
  const throwCountRef = useRef(0);
  const effectIdRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const [promptVisible, setPromptVisible] = useState(false);
  const [interactionStarted, setInteractionStarted] = useState(false);
  const [throwCount, setThrowCount] = useState(0);
  const [effects, setEffects] = useState<TomatoThrowEffect[]>([]);
  const [goldCelebrated, setGoldCelebrated] = useState(false);
  const [isCardExiting, setIsCardExiting] = useState(false);

  const schedule = useCallback((callback: () => void, delay: number) => {
    const timerId = window.setTimeout(() => {
      scheduledTimersRef.current.delete(timerId);
      callback();
    }, delay);
    scheduledTimersRef.current.add(timerId);
    return timerId;
  }, []);

  const clearRuntimeEffects = useCallback(() => {
    if (promptTimeoutRef.current !== null) {
      window.clearTimeout(promptTimeoutRef.current);
      scheduledTimersRef.current.delete(promptTimeoutRef.current);
      promptTimeoutRef.current = null;
    }

    resumeVideoTimerRef.current = null;
    resumePlaybackRef.current = false;

    scheduledTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    scheduledTimersRef.current.clear();
    activeAudioRef.current.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    activeAudioRef.current.clear();
  }, []);

  useEffect(() => clearRuntimeEffects, [clearRuntimeEffects]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    void video.play().catch(() => setIsPlaying(false));
  }, []);

  function playEffectAudio(path: string, volume: number) {
    const audio = new Audio(assetUrl(path));
    audio.volume = volume;
    activeAudioRef.current.add(audio);
    const release = () => activeAudioRef.current.delete(audio);
    audio.addEventListener("ended", release, { once: true });
    audio.addEventListener("error", release, { once: true });
    void audio.play().catch(release);
  }

  function showPromptOnce() {
    if (promptShownRef.current || interactionStarted) {
      return;
    }

    promptShownRef.current = true;
    setPromptVisible(true);
    const video = videoRef.current;
    if (video && !video.paused && !video.ended) {
      resumePlaybackRef.current = true;
      video.pause();
    }
    promptTimeoutRef.current = schedule(() => {
      promptTimeoutRef.current = null;
      setPromptVisible(false);
      const activeVideo = videoRef.current;
      if (activeVideo && !activeVideo.ended && resumePlaybackRef.current) {
        void activeVideo.play().catch(() => setIsPlaying(false));
      }
      resumePlaybackRef.current = false;
    }, PROMPT_TIMEOUT_MS);
  }

  function launchTomato() {
    if (throwCountRef.current >= MAX_THROWS) {
      return;
    }

    if (promptTimeoutRef.current !== null) {
      window.clearTimeout(promptTimeoutRef.current);
      scheduledTimersRef.current.delete(promptTimeoutRef.current);
      promptTimeoutRef.current = null;
    }

    const nextCount = throwCountRef.current + 1;
    const variant: TomatoVariant = nextCount === MAX_THROWS ? "gold" : "red";
    const origin = THROW_ORIGINS[nextCount - 1];
    const video = videoRef.current;
    const target = getFaceTarget(video?.currentTime ?? 0);
    const effectId = ++effectIdRef.current;
    const midX = (origin.x + target.x) / 2 + origin.curveX;
    const midY = Math.min(origin.y, target.y) - 280;

    throwCountRef.current = nextCount;
    setThrowCount(nextCount);
    setInteractionStarted(true);
    setIsCardExiting(false);
    setPromptVisible(false);
    setEffects((current) => [
      ...current,
      {
        id: effectId,
        variant,
        startX: origin.x,
        startY: origin.y,
        midX,
        midY,
        targetX: target.x,
        targetY: target.y,
        startRotation: origin.rotation,
        endRotation: origin.rotation > 0 ? origin.rotation + 520 : origin.rotation - 520
      }
    ]);

    if (video) {
      if (!video.paused && !video.ended) {
        resumePlaybackRef.current = true;
      }
      video.pause();
    }
    if (resumeVideoTimerRef.current !== null) {
      window.clearTimeout(resumeVideoTimerRef.current);
      scheduledTimersRef.current.delete(resumeVideoTimerRef.current);
    }
    if (nextCount === MAX_THROWS) {
      schedule(() => setIsCardExiting(true), RESUME_AFTER_THROW_MS - CARD_EXIT_DURATION_MS);
      resumeVideoTimerRef.current = schedule(() => {
        resumeVideoTimerRef.current = null;
        setInteractionStarted(false);
        setIsCardExiting(false);
        const activeVideo = videoRef.current;
        if (activeVideo && !activeVideo.ended && resumePlaybackRef.current) {
          void activeVideo.play().catch(() => setIsPlaying(false));
        }
        resumePlaybackRef.current = false;
      }, RESUME_AFTER_THROW_MS);
    }

    playEffectAudio(TOMATO_ASSETS.whoosh, 0.78);
    schedule(() => {
      playEffectAudio(variant === "gold" ? TOMATO_ASSETS.impactGold : TOMATO_ASSETS.impactRed, 0.92);
      if (variant === "gold") {
        setGoldCelebrated(true);
      }
    }, IMPACT_DELAY_MS);
    schedule(() => {
      setEffects((current) => current.filter((effect) => effect.id !== effectId));
    }, EFFECT_LIFETIME_MS);
  }

  function resetDemo() {
    clearRuntimeEffects();
    promptShownRef.current = false;
    throwCountRef.current = 0;
    setPromptVisible(false);
    setInteractionStarted(false);
    setThrowCount(0);
    setEffects([]);
    setGoldCelebrated(false);
    setIsCardExiting(false);
    setVideoEnded(false);

    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.currentTime = 0;
    void video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
  }

  function togglePlayback() {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (video.paused) {
      if (video.ended) {
        video.currentTime = 0;
        setVideoEnded(false);
      }
      void video.play().catch(() => setIsPlaying(false));
      return;
    }

    video.pause();
  }

  return (
    <section className="night-tomato-demo" aria-label="扔番茄互动演示">
      <video
        ref={videoRef}
        className="night-tomato-demo__video"
        src={assetUrl(TOMATO_ASSETS.clip)}
        autoPlay
        playsInline
        preload="auto"
        onEnded={() => {
          setIsPlaying(false);
          setVideoEnded(true);
        }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => {
          setIsPlaying(true);
          setVideoEnded(false);
        }}
        onTimeUpdate={(event) => {
          if (event.currentTarget.currentTime >= TRIGGER_TIME_SECONDS) {
            showPromptOnce();
          }
        }}
      />
      <div className="night-tomato-demo__shade" aria-hidden="true" />

      <button className="night-tomato-demo__close" type="button" onClick={onClose} aria-label="关闭扔番茄演示">
        <X size={30} />
      </button>

      <button
        className="night-tomato-demo__play-toggle"
        type="button"
        onClick={videoEnded ? resetDemo : togglePlayback}
        aria-label={videoEnded ? "重新播放演示" : isPlaying ? "暂停视频" : "继续播放视频"}
      >
        {videoEnded ? <RotateCcw size={32} /> : isPlaying ? <Pause size={32} /> : <Play size={32} fill="currentColor" />}
      </button>

      {promptVisible ? (
        <TomatoActionCard
          evaSrc={evaSrc}
          message="这个人也太坏了，要不要扔番茄解解气"
          actionLabel="扔一个"
          actionAriaLabel="扔出第一个番茄"
          onAction={launchTomato}
        />
      ) : null}

      {interactionStarted ? (
        <TomatoActionCard
          key={throwCount}
          evaSrc={evaSrc}
          message="这个人也太坏了，要不要扔番茄解解气"
          actionLabel="扔一个"
          actionAriaLabel={
            throwCount >= MAX_THROWS
              ? "番茄连击完成"
              : throwCount === MAX_THROWS - 1
                ? "扔出第三个番茄"
                : "扔出第二个番茄"
          }
          comboCount={throwCount}
          disabled={throwCount >= MAX_THROWS}
          isExiting={isCardExiting}
          onAction={launchTomato}
        />
      ) : null}

      {effects.map((effect) => {
        const projectileSrc = effect.variant === "gold" ? TOMATO_ASSETS.projectileGold : TOMATO_ASSETS.projectileRed;
        const splashSrc = effect.variant === "gold" ? TOMATO_ASSETS.splashGold : TOMATO_ASSETS.splashRed;
        const style = {
          "--tomato-start-x": `${effect.startX}px`,
          "--tomato-start-y": `${effect.startY}px`,
          "--tomato-mid-x": `${effect.midX}px`,
          "--tomato-mid-y": `${effect.midY}px`,
          "--tomato-target-x": `${effect.targetX}px`,
          "--tomato-target-y": `${effect.targetY}px`,
          "--tomato-start-rotation": `${effect.startRotation}deg`,
          "--tomato-end-rotation": `${effect.endRotation}deg`
        } as MotionStyle;

        return (
          <div
            key={effect.id}
            className={`night-tomato-effect night-tomato-effect--${effect.variant}`}
            style={style}
            aria-hidden="true"
          >
            <img className="night-tomato-effect__projectile" src={assetUrl(projectileSrc)} alt="" draggable={false} />
            <span className="night-tomato-effect__impact" />
            <img className="night-tomato-effect__splash" src={assetUrl(splashSrc)} alt="" draggable={false} />
          </div>
        );
      })}

      {goldCelebrated ? <div className="night-tomato-gold-burst" aria-hidden="true" /> : null}
    </section>
  );
}
